//@flow
/**
 * Learner actions
 * @module learner/actions
 */
import ReactGA from 'react-ga'
import { getProposedQuizId } from './lib'
import {
  UPDATE_LEARNER_NOTES,
  UPDATE_LEARNER_NOTES_REQUEST,
  UPDATE_LEARNER_NOTES_SUCCESS,
  UPDATE_LEARNER_NOTES_FAILURE,
  SUBMIT_LEARNER_FORM_REQUEST,
  SUBMIT_LEARNER_FORM_PENDING,
  SUBMIT_LEARNER_FORM_SUCCESS,
  SUBMIT_LEARNER_FORM_FAILURE,
  FINISH_QUIZ_LATER_REQUEST,
  FINISH_QUIZ_LATER_SUCCESS,
  FINISH_QUIZ_LATER_FAILURE,
  SUBMIT_ANSWER_REQUEST,
  SUBMIT_ANSWER_PENDING,
  SUBMIT_ANSWER_SUCCESS,
  SUBMIT_ANSWER_FAILURE,
  RESTORE_LEARNER_REQUEST,
  RESTORE_LEARNER_FAILURE,
  RESTORE_LEARNER_SUCCESS,
  UPDATE_DLRQ_LEARNER,
  COMPLETE_DLRQ_LEARNER,
  HIDE_ERROR_SUBMISSION,
  LOADING_PAGE,
  COMPLETE_DLRQ_LEARNER_SUCCESS,
  COMPLETE_DLRQ_LEARNER_FAILURE
} from './reducer'

import socket, { whenConnected } from '../../socket'
import { displayInvalidInviteScreen, getClientId } from '../../utils'

import {
  getNextQuestionId,
  canProgressToQuestion
} from '@lrg/lln-quiz-common/lib/learner/lib'
import { preloadQuestions } from '../questions/lib'
import {
  addInviteToLocation,
  submitDlrqResult,
  redirectToShortQuiz
} from './lib'
import { allTestLevels } from '../../constants'
import { dlrqQuestions, dlrqSuitabilityMatrix } from '../../constants/dlrq'
import { saveLearnerForm, submitLearnerAnswer, submitCourseReady } from '../../utils/api/learner'

/**
 * Sends request to finish quiz later, triggering an email to a learner
 * @memberof learner/actions
 */
export const finishQuizLater = () => (dispatch: Function) => {
  dispatch({
    type: FINISH_QUIZ_LATER_REQUEST
  })
  socket.emit('learner:completeLater', (err?: Error): void => {
    if (err) {
      return dispatch({
        type: FINISH_QUIZ_LATER_FAILURE,
        payload: err,
        error: true
      })
    }
    dispatch({
      type: FINISH_QUIZ_LATER_SUCCESS
    })
    ReactGA.event({
      category: 'Learner',
      action: 'Finish quiz later'
    })
  })
}

/**
 * Sets a learners notes and triggers a throttled update to the backend
 * @memberof learner/actions
 */
let updateLearnerNotesTimeout
export const setNotes = (notes: string) => (dispatch: Function) => {
  dispatch({
    type: UPDATE_LEARNER_NOTES,
    payload: notes
  })

  clearTimeout(updateLearnerNotesTimeout)
  updateLearnerNotesTimeout = setTimeout(() => {
    dispatch({
      type: UPDATE_LEARNER_NOTES_REQUEST
    })
    socket.emit('learner:notes', notes, (err?: Error): void => {
      if (err) {
        return dispatch({
          type: UPDATE_LEARNER_NOTES_FAILURE,
          payload: err,
          error: true
        })
      }
      dispatch({
        type: UPDATE_LEARNER_NOTES_SUCCESS,
        payload: notes
      })
      ReactGA.event({
        category: 'Learner',
        action: 'Updated notes'
      })
    })
  }, 3000)
}

/**
 * Submits the learners form, updating a learner if registered or creating if not
 * @memberof learner/actions
 */
export const submitLearnerForm =
  (inputs: { [key: string]: { value: string, skipped: boolean } }) =>
  (dispatch: Function, getState: Function) => {
    dispatch({
      type: SUBMIT_LEARNER_FORM_REQUEST
    })

    const {
      client,
      learner: { testLevel, id: learnerId, invite = null }
    } = getState()
    const {
      learnerId: learnerIdInput,
      firstname,
      lastname,
      email,
      courses,
      ...customInputs
    } = inputs
    const learnerDetails = {}
    learnerDetails.learnerId = learnerIdInput.value
    
    learnerDetails.firstname = firstname.value
    
    learnerDetails.lastname = lastname.value

    learnerDetails.selectedCourseIdComparison = courses && courses.value === "I'm not sure which course I'm interested in." ? "default" : courses && courses.value ?  courses.value : null;
    
    learnerDetails.email = email.value
    learnerDetails.custom = Array.from(Object.entries(customInputs))
      .filter(([, val = {}]) => val != null && typeof val === 'object')
      .map(([_id, val = {}]) => {
        const { value, skipped } = val
        let data = {}
        data[_id]= value
        return data
      })

    if (learnerId) {
      learnerDetails._id = learnerId
    }

    const proposedQuizId = getProposedQuizId()
    // only set quiz id if it is valid and learner doesn't already have a quizId
    if (
      testLevel === '' &&
      proposedQuizId != null &&
      allTestLevels.includes(proposedQuizId)
    ) {
      learnerDetails.quizId = proposedQuizId
    } else {
      // default to current testLevel, defaulting to client default, and defaulting to acsf3
      learnerDetails.quizId =
        testLevel || client.getIn(['preferences', 'defaultQuizLevel'], 'acsf3')
    }

    var dispatches = [
      {
        type: SUBMIT_LEARNER_FORM_PENDING,
        payload: learnerDetails
      }
    ]

    if (learnerDetails.quizId === 'dlrq') {
      // we don't want to preload questions for the DLRQ quiz and initial question is set
      learnerDetails.initialQuestionRequest = 's0-q1'
      learnerDetails.state = { currentQuestion: 's0-q1', complete: 'false' }
    } else {
      // traditional quiz the questions are randomised
      // learnerDetails.initialQuestionRequest = getNextQuestionId(
      //   learnerDetails.quizId,
      //   []
      // )
      // and we want to preload the question and resources, not required for dlrq
      // dispatches.push(preloadQuestions)
    }

    // dispatch(dispatches)

    // schedule learner sumission and asume success
    // NOTES: This is probably what is causing the issue where learner answers end up on different quizzes
    whenConnected(async immediate => {
      try {
        // const learner = await submitLearner(learnerDetails)
        if (invite) {
          learnerDetails.invite = invite;
        }
        const submitFormData = await saveLearnerForm(learnerDetails);
        const learner = submitFormData.data.data;
        addInviteToLocation(learner.inviteCode)

        // avoid setting question if quiz has already begun
        // if (immediate && learner.state.currentQuestion === null) {
        //   // learner.state.currentQuestion = getNextQuestionId(
        //   //   learner.testLevel,
        //   //   learner.answers
        //   // )
        //   console.log('----------------------------------------------------------------', learner.state.currentQuestion);
        //   learner.state.currentQuestion = learner.state.currentQuestion;
        // }
        learnerDetails.initialQuestionRequest =learner.state.currentQuestion;
        
        
        dispatch(dispatches)
        dispatch({
          type: SUBMIT_LEARNER_FORM_SUCCESS,
          payload: learner
        })

        ReactGA.set({ userId: `${getClientId()}:${learner.inviteCode}` })
        ReactGA.event({
          category: 'Learner',
          action: 'Submitted Learner Form'
        })
      } catch (err) {
        dispatch({
          type: SUBMIT_LEARNER_FORM_FAILURE,
          payload: err,
          error: true
        })
      }
    })
  }

/**
 * Determines the next question id and schedules a question submission.
 * @memberof learner/actions
 */
export function submitAnswer (
  questionId: string,
  answer: { answer: RawLearnerAnswer, correct: boolean },
  startTime: number
): Function {
  return async (dispatch: Function, getState: Function) => {
    const { learner, questions } = getState()
    const submittingAnswer = learner.get('submittingAnswer', false)
    dispatch({
      type: LOADING_PAGE
    })
    if (submittingAnswer) return
    dispatch({
      type: SUBMIT_ANSWER_REQUEST,
      payload: {
        questionId,
        answer,
        startTime,
        endTime: Date.now()
      }
    })
    // if any requested questions can be progressed to then request that question
    const answers = [
      ...learner.answers.toJS(),
      {
        questionId,
        answer
      }
    ]
    // request the next question from already requested questions, falling back to
    // questions in the preload path, finally defaulting to quiz finished
    const nextQuestionRequest =
      questions.requested.find(id =>
        canProgressToQuestion(learner.testLevel, id, answers)
      ) ||
      questions.preloadPath.find(id =>
        canProgressToQuestion(learner.testLevel, id, answers)
      ) ||
      'false'
    const questionSubmission = {
      answer: {
        questionId,
        answer: answer.answer,
        correct: answer.correct,
        startTime,
        submitTime: Date.now()
      },
      nextQuestionRequest
    }

    // dispatch({
    //   type: SUBMIT_ANSWER_PENDING,
    //   payload: questionSubmission
    // })

    whenConnected(async () => {
      try {
        // await whenLearnerRegistered()
        questionSubmission.invite = learner.invite;
        // const response = await submitQuestion(questionSubmission)
        const response = await submitLearnerAnswer(questionSubmission);
        questionSubmission.nextQuestionRequest = response.data.data.nextQuestion
          dispatch({
            type: SUBMIT_ANSWER_PENDING,
            payload: questionSubmission
          })

        dispatch({
          type: SUBMIT_ANSWER_SUCCESS,
          payload: {
            response: response.data.data || {},
            questionSubmission
          }
        })
        ReactGA.event({
          category: 'Learner',
          action: 'Submitted Answer',
          label: questionId
        })
      } catch (err) {
        // if already submitted ignore and move on to next question
        if (/answer already submitted/.test(err)) {
          dispatch({
            type: SUBMIT_ANSWER_SUCCESS,
            payload: {
              questionSubmission
            }
          })
        } else {
          return dispatch({
            type: SUBMIT_ANSWER_FAILURE,
            payload: err,
            error: true
          })
        }
      }
    })
  }
}

/**
 * Submits an answer for DLRQ questionnaire.  This is a special quiz type which
 * does not submit results to server every question, but instead submits all answers at the end
 * There is no "answer" processing, answers to current question determine the next question
 * @memberof learner/actions
 */
export function submitAnswerDlrq (
  questionId: string,
  selectedOptionIds: Array<string>
) {
  return async (dispatch: Function, getState: Function) => {
    const { learner, questions } = getState()
    const question = dlrqQuestions[questionId]
    const selectedOptions = question.options.filter(
      x => selectedOptionIds.indexOf(x.id) >= 0
    )
    const answer = { questionId, 
      selectedIds: selectedOptionIds, 
      questionText: question.text,
      selectedAnswers: selectedOptions.map(x => x.text)  
    }


    // Calculate the new score
    let { dlrqScore, dlrqWorkings } = learner
    const capability = calcScore(learner, 'capability', [
      answer,
      ...learner.dlrqResponses
    ])
    dlrqScore.capability = capability.score
    dlrqWorkings.capability = capability.workings

    const access = calcScore(learner, 'access', [
      answer,
      ...learner.dlrqResponses
    ])
    dlrqScore.access = access.score
    dlrqWorkings.access = access.workings

    if (selectedOptions[0].baseline) dlrqScore.baseline = selectedOptions[0].baseline
    if (selectedOptions[0].accessBaseline) dlrqScore.accessBaseline = selectedOptions[0].accessBaseline
    if (selectedOptions[0].focusAreaBaseline) dlrqScore.focusAreaBaseline = selectedOptions[0].focusAreaBaseline
    for (let i = 0; i < 5; i++) {
      const faResult = calcFocusScore(learner, i, [
        answer,
        ...learner.dlrqResponses
      ]);
      dlrqScore.focusAreas[i] = faResult.score;
      dlrqWorkings.focusAreas[i] = faResult.workings;
    }

    let nextQuestionId = question.next || selectedOptions[0].next
    if (!nextQuestionId) {
      // NOTE: Conditions are only based on the Capability score at the moment,
      const condition = question.conditionalnext || selectedOptions[0].conditionalnext
      nextQuestionId =
        dlrqScore.capability >= condition.capability
          ? condition.next
          : condition.else
    }



    if (nextQuestionId !== 'finish') {
      // move to next question
      dispatch({
        type: UPDATE_DLRQ_LEARNER,
        payload: { answer, nextQuestionId, dlrqScore, dlrqWorkings }
      })
    } else {
      // build and submit the result
      const result = buildDlrqResult(dlrqScore)
      try {
        await submitCourseReady({result, learnerResponse: learner.dlrqResponses, invite: learner.invite})
        dispatch({
          type: COMPLETE_DLRQ_LEARNER_SUCCESS,
          payload: {
            result
          }
        })
        ReactGA.event({
          category: 'Learner',
          action: 'Questionaire Completed'
        })
      } catch (error) {
        dispatch({
          type: COMPLETE_DLRQ_LEARNER_FAILURE,
          payload: {
            result
          }
        })
      }
    }
  }
}

/**
 * Submits the DLRQ quiz results to the server
 */
function buildDlrqResult (score) {
  const suitabilityResult = dlrqSuitabilityMatrix[score.access][score.capability];
  return {
    capability: score.capability,
    access: score.access,
    suitabilityResult,
    focusArea: {
      digital: score.focusAreas[0],
      communication: score.focusAreas[1],
      content: score.focusAreas[2],
      protection: score.focusAreas[3],
      technical: score.focusAreas[4]
    }
  }
}

/**
 *
 */
function calcScore (learner, item, answers) {
  let total = 0,
    inputs = 0,
    workings = [] // workings just displayed for Dave during testing to validate calculations

  answers.forEach(answer => {
    const question = dlrqQuestions[answer.questionId]
    const selectedOptions = question.options.filter(
      x => answer.selectedIds.indexOf(x.id) >= 0
    )
    total += selectedOptions.reduce((total, x) => total + x[item], 0)
    inputs += selectedOptions.reduce((used, x) => used + (x[item] ? 1 : 0), 0)

    workings.push(...selectedOptions.filter(x => x[item] !== 0).map(x => x[item]))
  })

  const baseline = item === 'access' ? learner.dlrqScore.accessBaseline : learner.dlrqScore.baseline
  let score = Math.round(
    (baseline + total) / (inputs + 1) + baseline
  )
  score = Math.min(8, Math.max(0, score)) // make sure score is between 0 and 8

  return { score, workings }
}

/**
 * Focus areas are calculated slightly differently
 * - we don't add baseline to the averaged result
 * - and we divide by 3 if there are less than 3 adjustments
 */
function calcFocusScore (learner, index, answers) {
  let total = 0,
    inputs = 0,
    workings = [] // workings just displayed for Dave during testing to validate calculations

  answers.forEach(answer => {
    const question = dlrqQuestions[answer.questionId]
    const selectedOptions = question.options.filter(
      x => answer.selectedIds.indexOf(x.id) >= 0
    )
    total += selectedOptions.reduce(
      (total, x) => total + (x.focusarea ? x.focusarea[index] : 0),
      0
    )
    inputs += selectedOptions.reduce(
      (used, x) => used + (x.focusarea && x.focusarea[index] ? 1 : 0),
      0
    )

    workings.push(
      ...selectedOptions
        .filter(x => x.focusarea && x.focusarea[index] !== 0)
        .map(x => x.focusarea[index])
    )
  })

  const score = Math.round(
    (learner.dlrqScore.focusAreaBaseline + total) / Math.max(inputs, 3)
  )
  return { score, workings }
}

/**
 * Restores a learners state and registers them on the server
 * @memberof learner/actions
 */
export function restoreLearner (invite: string): Function {
  return dispatch => {
    dispatch({
      type: RESTORE_LEARNER_REQUEST
    })
    socket.emit(
      'learner:invite',
      {
        invite: invite
      },
      (err, response) => {
        if (err) {
          if (err === 'Learner already registered on server.') return
          if (err === 'Invalid invite.') {
            return displayInvalidInviteScreen(invite)
          }
          return dispatch({
            type: RESTORE_LEARNER_FAILURE,
            payload: err,
            error: true
          })
        }
        const { learner } = response
        console.log('learner restored', learner)

        // REDIRECT TO NEW UI
        redirectToShortQuiz(learner.testLevel, invite)

        if (learner.state.currentQuestion === null) {
          // if no next question if provided by the server, or not initialised then apply one
          learner.state.currentQuestion = getNextQuestionId(
            learner.testLevel,
            learner.answers
          )
        }
        dispatch([
          {
            type: RESTORE_LEARNER_SUCCESS,
            payload: learner || false
          },
          preloadQuestions
        ])
        ReactGA.set({ userId: `${getClientId()}:${invite}` })
        ReactGA.event({
          category: 'Learner',
          action: 'Restored learner'
        })
      }
    )
  }
}


/**
 * Hide error message
 * @memberof learner/actions
 */
export function hideErrorSubmission (): Function {
    return dispatch => {
        dispatch({
          type: HIDE_ERROR_SUBMISSION
        })
    };
};


// WEBPACK FOOTER //
// ./src/state/learner/actions.js